import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '../../lib/contracts';
import { FEATURE_ROUTES } from '../routeConstants';
import logo from '../../assets/ninpo-logo.png';
import logoSvg from '../../assets/ninpo-logo.svg';
import { getSocket } from '../../lib/socket';
import { formatCurrency } from '../../lib/formatters';
import { useCoreContext } from '../../lib/coreContext';
import { TricolorBrandWordmark } from './TricolorBrandWordmark';

interface Props {
  currentUserRole?: UserRole;
  isLoggedIn: boolean;
  onLogin: () => void;
  onLogout: () => void;
  onOpenSupport?: () => void;
}

const Header = ({
  currentUserRole,
  isLoggedIn,
  onLogin,
  onLogout,
  onOpenSupport
}: Props) => {
  const navigate = useNavigate();
  const core = useCoreContext();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [dietaryFilter, setDietaryFilter] = useState('All');

  const userRoles = useMemo(() => {
    if (core.currentUser?.roles && core.currentUser.roles.length > 0) {
      return core.currentUser.roles;
    }
    return currentUserRole ? [currentUserRole] : [UserRole.CUSTOMER];
  }, [core.currentUser?.roles, currentUserRole]);

  const isCustomer =
    !isLoggedIn ||
    userRoles.some((r) => {
      const s = String(r).toLowerCase();
      return s === 'customer' || s === 'driver';
    });
  const isStaff = userRoles.some((r) => {
    const s = String(r).toLowerCase();
    return s === 'admin' || s === 'owner';
  });

  const matchingProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    const liveProducts = core.products || [];
    return liveProducts.filter(p => {
      const nameMatch = (p.name || '').toLowerCase().includes(q) || (p.brand || '').toLowerCase().includes(q);
      const catMatch = (p.category || '').toLowerCase().includes(q);
      if (!nameMatch && !catMatch) return false;
      if (dietaryFilter === 'All') return true;
      const filterLower = dietaryFilter.toLowerCase();
      return (p.dietaryTags || []).some(t => t.toLowerCase() === filterLower);
    });
  }, [core.products, searchQuery, dietaryFilter]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const handleSocketConnect = () => setIsSocketConnected(true);
    const handleSocketDisconnect = () => setIsSocketConnected(false);

    const syncSocketStatus = () => {
      const s = getSocket();
      if (s) {
        setIsSocketConnected(s.connected);
        s.off('connect', handleSocketConnect);
        s.off('disconnect', handleSocketDisconnect);
        s.on('connect', handleSocketConnect);
        s.on('disconnect', handleSocketDisconnect);
      } else {
        setIsSocketConnected(false);
      }
    };

    syncSocketStatus();
    intervalId = setInterval(syncSocketStatus, 2500);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (intervalId) clearInterval(intervalId);
      const s = getSocket();
      if (s) {
        s.off('connect', handleSocketConnect);
        s.off('disconnect', handleSocketDisconnect);
      }
    };
  }, [isLoggedIn]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="px-4 pt-6 sm:px-6 sm:pt-8 max-w-[1600px] w-full mx-auto z-50 animate-in">
      <div className="glass-panel rounded-3xl p-4 sm:p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between neon-border-lime">

        {/* Logo + mobile menu (siblings — never nest interactive controls) */}
        <div className="flex items-center justify-between gap-3 sm:gap-4">
          <button
            type="button"
            className="flex items-center gap-3 sm:gap-4 cursor-pointer group text-left bg-transparent border-0 p-0"
            onClick={() => navigate('/')}
            aria-label="Ninpo Snacks home"
          >
            <div className="w-12 h-12 rounded-2xl border border-white/10 bg-ninpo-black/80 flex items-center justify-center shadow-neon-red group-hover:border-ninpo-red/50 transition-colors">
              <picture>
                <source type="image/svg+xml" srcSet={logoSvg} />
                <img
                  src={logo}
                  alt="Ninpo Snacks"
                  className="w-10 h-10 object-contain"
                  onError={(e) => {
                    const target = e.currentTarget;
                    if (target.dataset.fallbackApplied === '1') return;
                    target.dataset.fallbackApplied = '1';
                    target.src = '/icons/icon-192.png';
                  }}
                />
              </picture>
            </div>
            <TricolorBrandWordmark size="md" interactive={true} />
          </button>
          <button
            type="button"
            className={`sm:hidden ml-auto px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
              isMobileMenuOpen
                ? 'bg-ninpo-red/20 text-ninpo-red border border-ninpo-red/40 hover:bg-ninpo-red/30'
                : 'cyber-button'
            }`}
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            aria-expanded={isMobileMenuOpen}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? 'Close' : 'Menu'}
          </button>
        </div>

        {/* Header Auto-Suggest Search Bar */}
        <div className="relative flex-1 max-w-md mx-2 hidden md:block">
          <div className="relative flex items-center">
            <input
              type="text"
              placeholder="Search groceries, fresh foods, drinks, essentials..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
              aria-label="Search catalog"
              className="w-full px-4 py-2 bg-black/60 border border-white/10 rounded-2xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-ninpo-lime/60 transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                className="absolute right-3 text-slate-400 hover:text-white text-xs"
              >
                ✕
              </button>
            )}
          </div>

          {/* Auto-Suggest Search Dropdown */}
          {isSearchFocused && searchQuery.trim().length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-950/95 border border-white/10 rounded-2xl p-3 shadow-2xl backdrop-blur-xl z-50 space-y-2">
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-[10px]">
                {['All', 'Gluten-Free', 'Keto', 'Halal', 'Vegan'].map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setDietaryFilter(tag === dietaryFilter ? 'All' : tag)}
                    className={`px-2.5 py-1 rounded-full font-bold uppercase tracking-wider transition-all ${
                      dietaryFilter === tag
                        ? 'bg-ninpo-lime text-black'
                        : 'bg-white/5 border border-white/10 text-slate-400 hover:text-white'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>

              <div className="divide-y divide-white/5 max-h-60 overflow-y-auto">
                {matchingProducts.length === 0 ? (
                  <p className="p-3 text-xs text-slate-500 text-center font-bold">No matching items found</p>
                ) : (
                  matchingProducts.slice(0, 6).map(prod => {
                    const prodId = prod.frontendId || prod.id || prod._id || '';
                    const imgSrc = String(prod.image || prod.imageUrl || '').trim();
                    return (
                      <div
                        key={prodId}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          navigate(`${FEATURE_ROUTES.customer.shop}?q=${encodeURIComponent(prod.name)}`);
                          setSearchQuery('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`${FEATURE_ROUTES.customer.shop}?q=${encodeURIComponent(prod.name)}`);
                            setSearchQuery('');
                          }
                        }}
                        className="p-2 flex items-center justify-between hover:bg-white/5 rounded-xl cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-black/40 border border-white/10 p-0.5 shrink-0 overflow-hidden">
                            <img
                              src={imgSrc || '/icons/icon-192.png'}
                              alt={prod.name}
                              className="w-full h-full object-contain"
                              onError={e => {
                                e.currentTarget.src = '/icons/icon-192.png';
                              }}
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-white truncate">{prod.name}</p>
                            <p className="text-[10px] text-slate-400 uppercase font-black truncate">{prod.category || 'Grocery'}</p>
                          </div>
                        </div>
                        <span className="text-xs font-black text-ninpo-lime shrink-0 ml-2">{formatCurrency(prod.price || 0)}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Connection Status Badge */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-ninpo-midnight/60 border border-white/5 backdrop-blur-md shadow-inner text-[10px] font-black tracking-widest uppercase sm:ml-auto select-none">
          {!isOnline ? (
            <>
              <span className="w-2 h-2 rounded-full bg-ninpo-red animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.8)]"></span>
              <span className="text-ninpo-red neon-text-glow-red">Offline</span>
            </>
          ) : isLoggedIn && !isSocketConnected ? (
            <>
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shadow-[0_0_8px_rgba(245,158,11,0.8)]"></span>
              <span className="text-amber-500">Reconnecting</span>
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse"></span>
              <span className="text-emerald-500">Online</span>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="hidden sm:flex gap-3 w-full sm:w-auto sm:justify-end">
          {isCustomer && (
            <>
              <button
                onClick={() => navigate(FEATURE_ROUTES.customer.shop)}
                className="px-5 py-2.5 rounded-lg bg-white/5 text-slate-300 text-xs font-bold uppercase tracking-widest border border-white/10 hover:bg-white/10 hover:text-white transition-all"
              >
                Shop
              </button>
              <button
                onClick={() => navigate(FEATURE_ROUTES.customer.catalog)}
                className="px-5 py-2.5 rounded-lg bg-white/5 text-slate-300 text-xs font-bold uppercase tracking-widest border border-white/10 hover:bg-white/10 hover:text-white transition-all"
              >
                Catalog
              </button>
              {isLoggedIn ? (
                <button
                  onClick={() => navigate(FEATURE_ROUTES.customer.account)}
                  className="px-5 py-2.5 rounded-lg bg-white/5 text-slate-300 text-xs font-bold uppercase tracking-widest border border-white/10 hover:bg-white/10 hover:text-white transition-all"
                >
                  Account
                </button>
              ) : null}
              <button
                onClick={() => navigate(FEATURE_ROUTES.driver.onboarding)}
                className="px-4 py-2.5 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold uppercase tracking-widest border border-emerald-500/20 hover:bg-emerald-500/20 hover:text-emerald-300 transition-all"
              >
                Drive
              </button>
            </>
          )}
          {isStaff && (
            <>
              <button
                onClick={() =>
                  navigate(
                    userRoles.includes(UserRole.OWNER)
                      ? FEATURE_ROUTES.admin.productQueue
                      : FEATURE_ROUTES.admin.home
                  )
                }
                className="px-5 py-2.5 rounded-lg cyber-button text-xs font-bold uppercase tracking-widest"
              >
                {userRoles.includes(UserRole.OWNER) ? 'Owner console' : 'Admin'}
              </button>
            </>
          )}



          <button
            onClick={isLoggedIn ? onLogout : onLogin}
            className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-red-600/80 to-pink-600/80 text-white text-xs font-bold uppercase tracking-widest border border-red-500/30 hover:from-red-600 hover:to-pink-500 hover:shadow-[0_0_15px_rgba(239,68,68,0.4)] transition-all"
          >
            {isLoggedIn ? 'Logout' : 'Sign In'}
          </button>
        </div>
        {isMobileMenuOpen && (
          <div className="sm:hidden w-full rounded-2xl border border-white/5 bg-ninpo-midnight/95 p-4 flex flex-col gap-2">
            {isCustomer && (
              <>
                <button
                  onClick={() => handleNavigate(FEATURE_ROUTES.customer.shop)}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 text-slate-300 text-xs font-bold uppercase tracking-widest border border-white/10 hover:bg-white/10 transition-all"
                >
                  Shop
                </button>
                <button
                  onClick={() => handleNavigate(FEATURE_ROUTES.customer.catalog)}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 text-slate-300 text-xs font-bold uppercase tracking-widest border border-white/10 hover:bg-white/10 transition-all"
                >
                  Catalog
                </button>
                {isLoggedIn ? (
                  <button
                    onClick={() => handleNavigate(FEATURE_ROUTES.customer.account)}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 text-slate-300 text-xs font-bold uppercase tracking-widest border border-white/10 hover:bg-white/10 transition-all text-left"
                  >
                    Account
                  </button>
                ) : null}
                <button
                  onClick={() => handleNavigate(FEATURE_ROUTES.driver.onboarding)}
                  className="w-full px-4 py-3 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold uppercase tracking-widest border border-emerald-500/20 hover:bg-emerald-500/20 transition-all text-left flex items-center justify-between"
                >
                  <span>Apply to Drive</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">Driver Fleet</span>
                </button>
              </>
            )}
            {isStaff && (
              <>
                <button
                  onClick={() =>
                    handleNavigate(
                      userRoles.includes(UserRole.OWNER)
                        ? FEATURE_ROUTES.admin.productQueue
                        : FEATURE_ROUTES.admin.home
                    )
                  }
                  className="w-full px-4 py-3 rounded-lg cyber-button text-xs font-bold uppercase tracking-widest"
                >
                  {userRoles.includes(UserRole.OWNER) ? 'Owner console' : 'Admin'}
                </button>
              </>
            )}


            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                if (isLoggedIn) {
                  onLogout();
                } else {
                  onLogin();
                }
              }}
              className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-red-600/80 to-pink-600/80 text-white text-xs font-bold uppercase tracking-widest border border-red-500/30 transition-all"
            >
              {isLoggedIn ? 'Logout' : 'Sign In'}
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
